/**
 * Knowledge Repository — Document Ingestion & Intelligence
 *
 * Upload operational documents (Excel, PDF, Word, CSV, etc.)
 * Rex extracts, categorises, and indexes them so Ask Rex
 * can answer operational questions and proactively generate plans.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Search, FileText, Upload, Trash2, ChevronRight, X,
  FileSpreadsheet, File, FileImage, FolderOpen,
  BookOpen, Tag, Lightbulb, Clock, CheckCircle2,
  AlertTriangle, Loader2, Filter, RefreshCw,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["all", "Policy", "Process", "Client Intel", "Case Study", "Template", "Delivery", "Operations", "General"];

const CATEGORY_STYLES = {
  Policy:        "bg-rose-50 text-rose-700 border-rose-200",
  Process:       "bg-blue-50 text-blue-700 border-blue-200",
  "Client Intel":"bg-amber-50 text-amber-700 border-amber-200",
  "Case Study":  "bg-emerald-50 text-emerald-700 border-emerald-200",
  Template:      "bg-purple-50 text-purple-700 border-purple-200",
  Delivery:      "bg-teal-50 text-teal-700 border-teal-200",
  Operations:    "bg-indigo-50 text-indigo-700 border-indigo-200",
  General:       "bg-slate-50 text-slate-600 border-slate-200",
};

const FILE_ICONS = {
  pdf:  <FileText className="w-5 h-5 text-red-500" />,
  docx: <FileText className="w-5 h-5 text-blue-500" />,
  doc:  <FileText className="w-5 h-5 text-blue-500" />,
  xlsx: <FileSpreadsheet className="w-5 h-5 text-emerald-600" />,
  xls:  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />,
  csv:  <FileSpreadsheet className="w-5 h-5 text-emerald-500" />,
  pptx: <FileImage className="w-5 h-5 text-orange-500" />,
  txt:  <File className="w-5 h-5 text-slate-500" />,
  md:   <File className="w-5 h-5 text-slate-500" />,
};

const ACCEPTED_TYPES = ".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.txt,.md,.json,.xml,.html,.rtf";

export default function KnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // ─── Data Fetching ──────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory && activeCategory !== "all") params.set("category", activeCategory);
      const res = await fetch(`${API}/documents?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDocuments(Array.isArray(data) ? data : []);
      }
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // ─── Upload ─────────────────────────────────────────────────

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length, filename: file.name });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "General");

      try {
        const res = await fetch(`${API}/documents/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (res.ok) {
          successCount++;
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(`Failed: ${file.name} — ${err.detail || "Upload error"}`);
          failCount++;
        }
      } catch {
        toast.error(`Failed: ${file.name} — Network error`);
        failCount++;
      }
    }

    setUploading(false);
    setUploadProgress(null);

    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? "s" : ""} uploaded — Rex is extracting knowledge`);
      fetchDocuments();
    }
    if (failCount > 0 && successCount === 0) {
      toast.error(`All ${failCount} uploads failed`);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files) handleFileUpload(Array.from(files));
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  // ─── Search ─────────────────────────────────────────────────

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ query: searchQuery });
      if (activeCategory && activeCategory !== "all") params.append("category", activeCategory);
      const res = await fetch(`${API}/knowledge-base/search?${params}`, { credentials: "include" });
      if (res.ok) setSearchResults(await res.json());
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────

  const handleDelete = async (docId) => {
    if (!confirm("Remove this document from Rex's knowledge?")) return;
    try {
      const res = await fetch(`${API}/documents/${docId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Document removed");
        setDocuments(prev => prev.filter(d => d.document_id !== docId));
        if (selectedDoc?.document_id === docId) setSelectedDoc(null);
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  const getFileIcon = (fileType) => FILE_ICONS[fileType] || <File className="w-5 h-5 text-slate-400" />;

  const filteredDocs = activeCategory === "all" ? documents : documents.filter(d => d.category === activeCategory);

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="p-6" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-bold tracking-tight text-slate-800">Knowledge Repository</h1>
            <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 ml-1">
              {documents.length} docs
            </Badge>
          </div>
          <p className="text-sm text-slate-500">
            Upload your documents — Rex extracts knowledge and uses it to answer operational questions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {uploadProgress?.current}/{uploadProgress?.total}...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload Documents</>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(Array.from(e.target.files))}
            data-testid="file-input"
          />
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex gap-2 mb-4" data-testid="knowledge-search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="knowledge-search-input"
            placeholder="Search Rex's knowledge — ask anything about your operations, processes, delivery..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10 h-10"
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={searching}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="category-filter">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => { setActiveCategory(cat); setSearchResults(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              activeCategory === cat
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
          >
            {cat === "all" ? "All" : cat}
          </button>
        ))}
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="mb-6" data-testid="search-results">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">
              Search Results ({searchResults.documents?.length || 0} documents)
            </h3>
            <button onClick={() => { setSearchResults(null); setSearchQuery(""); }} className="text-xs text-slate-500 hover:text-slate-700">
              Clear results
            </button>
          </div>
          {(searchResults.documents?.length || 0) === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
              <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No results found for "{searchQuery}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {searchResults.documents.map((doc) => (
                <div
                  key={doc.document_id}
                  className="rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                  onClick={() => setSelectedDoc(doc)}
                  data-testid={`search-result-${doc.document_id}`}
                >
                  <div className="flex items-start gap-3">
                    {getFileIcon(doc.file_type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{doc.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{doc.summary || doc.snippet || ""}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {doc.category && (
                          <Badge variant="outline" className={`text-[9px] ${CATEGORY_STYLES[doc.category] || CATEGORY_STYLES.General}`}>
                            {doc.category}
                          </Badge>
                        )}
                        {doc.tags?.slice(0, 3).map((t) => (
                          <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Content: Drop Zone + Document List + Detail */}
      <div className="flex gap-4">
        <div className={`${selectedDoc ? "w-1/2" : "w-full"} transition-all`}>
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`rounded-xl border-2 border-dashed p-6 mb-4 text-center transition-all cursor-pointer ${
              dragOver
                ? "border-blue-500 bg-blue-50"
                : "border-slate-200 bg-slate-50/50 hover:border-slate-300"
            }`}
            onClick={() => fileInputRef.current?.click()}
            data-testid="drop-zone"
          >
            <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-blue-500" : "text-slate-400"}`} />
            <p className="text-sm text-slate-600 font-medium">
              {dragOver ? "Drop files here" : "Drag & drop files here, or click to browse"}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">
              PDF, Excel, Word, CSV, PowerPoint, Text — up to 20MB per file
            </p>
            {uploading && uploadProgress && (
              <div className="mt-3">
                <div className="w-48 mx-auto bg-slate-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-blue-600 mt-1">
                  Processing {uploadProgress.filename} ({uploadProgress.current}/{uploadProgress.total})
                </p>
              </div>
            )}
          </div>

          {/* Document List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-slate-700 mb-1">
                {activeCategory !== "all" ? `No ${activeCategory} documents` : "No documents yet"}
              </h3>
              <p className="text-xs text-slate-500">
                Upload your operational sheets, checklists, and processes — Rex will learn from them
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto" data-testid="document-list">
              {filteredDocs.map((doc) => (
                <div
                  key={doc.document_id}
                  className={`rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm ${
                    selectedDoc?.document_id === doc.document_id
                      ? "border-blue-400 bg-blue-50/50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedDoc(doc)}
                  data-testid={`doc-card-${doc.document_id}`}
                >
                  <div className="flex items-start gap-3">
                    {getFileIcon(doc.file_type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{doc.summary || "Processing..."}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {doc.category && (
                          <Badge variant="outline" className={`text-[9px] ${CATEGORY_STYLES[doc.category] || CATEGORY_STYLES.General}`}>
                            {doc.category}
                          </Badge>
                        )}
                        <span className="text-[10px] text-slate-400">{formatSize(doc.file_size)}</span>
                        <span className="text-[10px] text-slate-400">{formatDate(doc.created_at)}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(doc.document_id); }}
                      className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                      data-testid={`delete-doc-${doc.document_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Document Detail Panel */}
        {selectedDoc && (
          <div className="w-1/2 sticky top-0">
            <DocumentDetail
              doc={selectedDoc}
              onClose={() => setSelectedDoc(null)}
              formatDate={formatDate}
              formatSize={formatSize}
              getFileIcon={getFileIcon}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Document Detail Panel ──────────────────────────────────────

function DocumentDetail({ doc, onClose, formatDate, formatSize, getFileIcon }) {
  const [fullDoc, setFullDoc] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => {
    const fetchFull = async () => {
      setLoadingContent(true);
      try {
        // Fetch full content
        const res = await fetch(`${API}/documents`, { credentials: "include" });
        if (res.ok) {
          const docs = await res.json();
          const found = (Array.isArray(docs) ? docs : []).find(d => d.document_id === doc.document_id);
          setFullDoc(found || doc);
        }
      } catch { setFullDoc(doc); }
      setLoadingContent(false);
    };
    fetchFull();
  }, [doc]);

  const display = fullDoc || doc;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm max-h-[calc(100vh-280px)] overflow-y-auto" data-testid="doc-detail">
      {/* Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-3">
            {getFileIcon(display.file_type)}
            <div>
              <h2 className="text-base font-bold text-slate-800">{display.name}</h2>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                <span>{display.file_type?.toUpperCase()}</span>
                <span>{formatSize(display.file_size)}</span>
                <span>{formatDate(display.created_at)}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        {display.category && (
          <Badge variant="outline" className={`text-[9px] ${CATEGORY_STYLES[display.category] || CATEGORY_STYLES.General}`}>
            {display.category}
          </Badge>
        )}
      </div>

      {/* AI Summary */}
      {display.summary && (
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Rex's Summary</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{display.summary}</p>
        </div>
      )}

      {/* Key Facts */}
      {display.key_facts?.length > 0 && (
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Key Facts Extracted</h3>
          </div>
          <ul className="space-y-1.5">
            {display.key_facts.map((fact, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <ChevronRight className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                {fact}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tags */}
      {display.tags?.length > 0 && (
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-4 h-4 text-blue-500" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Tags</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {display.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* How Rex Uses This */}
      <div className="p-5">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <p className="text-xs font-semibold text-blue-700">How Rex Uses This</p>
          </div>
          <p className="text-xs text-blue-600">
            This document is indexed and available to Ask Rex. You can ask questions like:
          </p>
          <div className="mt-2 space-y-1">
            {display.tags?.slice(0, 2).map((tag) => (
              <p key={tag} className="text-xs text-blue-500 italic">
                "What do we know about {tag}?"
              </p>
            ))}
            <p className="text-xs text-blue-500 italic">
              "What does {display.name?.split(".")[0]} say about...?"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
